/*
  card-video.js — autoplaying product videos on collection cards
  - No controls, muted, inline, looping (required for autoplay on every browser incl. iOS)
  - Starts downloading ~2 screens before the card arrives, starts playing just before it enters view
  - Fades in only once real frames are playing, so the product image stays visible until then (no black frame, no spinner, no play icon)
  - Pauses when the card leaves the screen, resumes when it comes back
*/
if (!customElements.get('card-video')) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Load zone: begin buffering when the card is within ~2 viewport heights.
  const loadObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.prepare();
        loadObserver.unobserve(entry.target);
      }
    });
  }, { rootMargin: '200% 0px 200% 0px' });

  // Play zone: play when the card is within a quarter screen of the viewport, pause otherwise.
  const playObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => entry.target.setActive(entry.isIntersecting));
  }, { rootMargin: '25% 0px 25% 0px', threshold: 0 });

  class CardVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video || reducedMotion.matches) return;

      const v = this.video;
      v.muted = true;
      v.defaultMuted = true;
      v.playsInline = true;
      v.setAttribute('muted', '');
      v.setAttribute('playsinline', '');
      v.setAttribute('webkit-playsinline', '');

      v.addEventListener('playing', () => this.reveal(), { once: true });
      v.addEventListener('timeupdate', () => { if (v.currentTime > 0) this.reveal(); }, { once: true });

      loadObserver.observe(this);
      playObserver.observe(this);
    }

    disconnectedCallback() {
      loadObserver.unobserve(this);
      playObserver.unobserve(this);
    }

    prepare() {
      if (this.prepared) return;
      this.prepared = true;
      const v = this.video;
      v.preload = 'auto';
      v.src = v.dataset.src;
      v.load();
      // iOS Safari ignores preload until playback is requested: start and immediately
      // pause the (muted, invisible) video so it buffers before the shopper reaches it.
      if (!this.active) {
        const p = v.play();
        if (p) p.then(() => { if (!this.active) v.pause(); }).catch(() => {});
      }
    }

    setActive(active) {
      this.active = active;
      if (!this.video) return;
      if (active) {
        this.prepare();
        const p = this.video.play();
        if (p) p.catch(() => {}); // e.g. iOS Low Power Mode: the product image simply stays
      } else {
        this.video.pause();
      }
    }

    reveal() {
      if (this.revealed) return;
      this.revealed = true;
      this.video.style.opacity = '1';
    }
  }

  customElements.define('card-video', CardVideo);
}
